import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { Role } from '../../../core/enums/role.enum';
import { SERVICO_RADIOTERAPIA_CNES_SESSAO_KEY } from '../../../core/constants/session-storage-keys';
import { CnesEstabelecimentoResumo } from '../models/cnes-estabelecimento-resumo.model';
import { CnesService } from './cnes.service';
import { EstabelecimentoService } from '../../identidade-usuarios/services/estabelecimento.service';
import {
  formatEstabelecimentoOptionLabel,
  normalizeNomeEstabelecimento,
} from '../../../shared/utils/estabelecimento-label.util';
import {
  SearchableSelectOption,
} from '../../../shared/components/searchable-select/searchable-select.component';

/**
 * Qualquer role do domínio "serviço de radioterapia" — inclui onboarding
 * (`cadastro-servico-*`) e operação granularizada (`paciente-*`, `equipamento-*`).
 * Usada para decidir se o usuário precisa de contexto CNES da sessão.
 */
const SERVICO_RADIOTERAPIA_ROLES: string[] = [
  Role.CADASTRO_SERVICO_VISUALIZAR,
  Role.CADASTRO_SERVICO_PREENCHER,
  Role.CADASTRO_SERVICO_AVALIAR,
  Role.PACIENTE_VIEW,
  Role.PACIENTE_ADD,
  Role.PACIENTE_EDIT,
  Role.EQUIPAMENTO_VIEW,
  Role.EQUIPAMENTO_ADD,
  Role.EQUIPAMENTO_EDIT,
  Role.EQUIPAMENTO_DELETE,
  Role.EQUIPAMENTO_AVALIAR,
];

function lerCnesValidoStorage(): string | null {
  try {
    const v = sessionStorage.getItem(SERVICO_RADIOTERAPIA_CNES_SESSAO_KEY)?.trim() ?? '';
    return v.length === 7 && /^\d{7}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Contexto de atuação (serviço de radioterapia): CNES do perfil (admin) ou seleção em sessão / API CNES.
 */
@Injectable({ providedIn: 'root' })
export class ServicoRadioterapiaCnesContextService {
  private auth = inject(AuthService);
  private cnesApi = inject(CnesService);
  private estabelecimentoService = inject(EstabelecimentoService);

  private readonly estabelecimentosSubject = new BehaviorSubject<CnesEstabelecimentoResumo[] | null>(null);
  readonly estabelecimentos$ = this.estabelecimentosSubject.asObservable();

  private readonly cnesSessaoSubject = new BehaviorSubject<string | null>(lerCnesValidoStorage());
  readonly cnesSessao$ = this.cnesSessaoSubject.asObservable();

  /** CNES vinculados ao usuário no cadastro interno (tb_usuario ↔ estabelecimento). */
  getCnesListNoPerfil(): string[] {
    const info = this.auth.getUserInfo();
    const list = (info?.cnesList ?? [])
      .map((c) => c.trim())
      .filter((c) => c.length === 7 && /^\d{7}$/.test(c));
    if (list.length > 0) return list;
    const single = info?.cnes?.trim() ?? '';
    return single.length === 7 && /^\d{7}$/.test(single) ? [single] : [];
  }

  /** CNES único no perfil (quando há exatamente um vínculo). */
  getCnesNoPerfil(): string {
    const list = this.getCnesListNoPerfil();
    return list.length === 1 ? list[0] : '';
  }

  /** CNES do token/perfil ou o escolhido na sessão. */
  getCnesEfetivo(): string {
    const p = this.getCnesNoPerfil();
    if (p) return p;
    return (this.cnesSessaoSubject.value ?? '').trim();
  }

  /**
   * Parâmetro `cnes` para APIs de radioterapia.
   * O backend usa a claim JWT quando presente; sem claim, exige `?cnes=` — sempre enviar o CNES efetivo do perfil/sessão.
   */
  buildCnesHttpParams(): HttpParams {
    const cnes = this.getCnesEfetivo().trim();
    if (cnes.length === 7 && /^\d{7}$/.test(cnes)) {
      return new HttpParams().set('cnes', cnes);
    }
    return new HttpParams();
  }

  setCnesSessao(cnes: string): void {
    const c = cnes.trim();
    if (c.length !== 7 || !/^\d{7}$/.test(c)) return;
    const permitidos = this.getCnesListNoPerfil();
    if (permitidos.length > 0 && !permitidos.includes(c)) return;
    sessionStorage.setItem(SERVICO_RADIOTERAPIA_CNES_SESSAO_KEY, c);
    this.cnesSessaoSubject.next(c);
  }

  clearCnesSessao(): void {
    sessionStorage.removeItem(SERVICO_RADIOTERAPIA_CNES_SESSAO_KEY);
    this.cnesSessaoSubject.next(null);
  }

  hasServicoRadioterapiaAccess(): boolean {
    return SERVICO_RADIOTERAPIA_ROLES.some((r) => this.auth.hasRole(r));
  }

  /** Múltiplos CNES no perfil ou sem CNES único: exige seleção de contexto. */
  precisaVincularCnesNaNavegacao(): boolean {
    if (!this.hasServicoRadioterapiaAccess()) return false;
    const list = this.getCnesListNoPerfil();
    if (list.length > 1) return !this.getCnesEfetivo();
    if (list.length === 1) return false;
    return !this.getCnesNoPerfil();
  }

  /** Pode abrir a tela de seleção/troca (mais de um estabelecimento disponível). */
  podeSelecionarEstabelecimento(): boolean {
    if (!this.hasServicoRadioterapiaAccess()) return false;
    const perfil = this.getCnesListNoPerfil();
    if (perfil.length > 1) return true;
    const cached = this.getEstabelecimentosCached();
    return (cached?.length ?? 0) > 1;
  }

  getEstabelecimentosCached(): CnesEstabelecimentoResumo[] | null {
    return this.estabelecimentosSubject.value;
  }

  loadEstabelecimentos(force = false): Observable<CnesEstabelecimentoResumo[]> {
    if (force) {
      this.estabelecimentosSubject.next(null);
    }

    const cached = this.estabelecimentosSubject.value;
    if (!force && cached !== null) {
      return of(cached);
    }

    const perfil = this.getCnesListNoPerfil();
    if (perfil.length > 0) {
      return this.carregarEstabelecimentosDoPerfil(perfil);
    }

    return this.cnesApi.listarMeusEstabelecimentos().pipe(
      map((list) => this.sanitizarLista(list)),
      tap((list) => this.estabelecimentosSubject.next(list)),
      catchError(() => {
        this.estabelecimentosSubject.next([]);
        return of([]);
      })
    );
  }

  mapParaOpcoesSelect(lista: CnesEstabelecimentoResumo[]): SearchableSelectOption[] {
    return lista.map((o) => ({
      value: o.cnes,
      label: formatEstabelecimentoOptionLabel(o.cnes, o.nomeEstabelecimento),
      searchText: `${o.cnes} ${o.nomeEstabelecimento}`.trim(),
    }));
  }

  private carregarEstabelecimentosDoPerfil(perfil: string[]): Observable<CnesEstabelecimentoResumo[]> {
    return this.estabelecimentoService.listar().pipe(
      switchMap((catalogo) => {
        const nomes = new Map(catalogo.map((e) => [e.cnes, e.nome]));
        const faltantes = perfil.filter((c) => !(nomes.get(c) ?? '').trim());
        if (faltantes.length === 0) {
          return of(this.montarListaResumo(perfil, nomes));
        }
        return forkJoin(
          faltantes.map((cnes) =>
            this.cnesApi.buscar(cnes).pipe(
              map((r) => ({ cnes, nome: (r.nomeServico ?? '').trim() })),
              catchError(() => of({ cnes, nome: '' }))
            )
          )
        ).pipe(map((extras) => {
          extras.forEach((x) => {
            if (x.nome) nomes.set(x.cnes, x.nome);
          });
          return this.montarListaResumo(perfil, nomes);
        }));
      }),
      tap((list) => this.estabelecimentosSubject.next(list)),
      catchError(() => {
        const list = this.montarListaResumo(perfil, new Map());
        this.estabelecimentosSubject.next(list);
        return of(list);
      })
    );
  }

  private montarListaResumo(
    cnesCodes: string[],
    nomes: Map<string, string>
  ): CnesEstabelecimentoResumo[] {
    return this.sanitizarLista(
      cnesCodes.map((cnes) => ({
        cnes,
        nomeEstabelecimento: nomes.get(cnes) ?? '',
      }))
    );
  }

  private sanitizarLista(list: CnesEstabelecimentoResumo[]): CnesEstabelecimentoResumo[] {
    return list.map((e) => ({
      cnes: e.cnes,
      nomeEstabelecimento: normalizeNomeEstabelecimento(e.cnes, e.nomeEstabelecimento),
    }));
  }

  /**
   * Após login: 0 CNES no token + vários no CNES → tela de contexto;
   * um único vínculo → grava sessão automaticamente.
   */
  resolvePostLoginNavigation(): Observable<string | null> {
    if (!this.precisaVincularCnesNaNavegacao()) {
      return of(null);
    }
    return this.loadEstabelecimentos(true).pipe(
      map((list) => {
        if (list.length === 0) return null;
        if (list.length === 1) {
          this.setCnesSessao(list[0].cnes);
          return null;
        }
        const saved = lerCnesValidoStorage();
        if (saved && list.some((e) => e.cnes === saved)) {
          this.cnesSessaoSubject.next(saved);
          return null;
        }
        this.clearCnesSessao();
        return '/servico-radioterapia/contexto-estabelecimento';
      })
    );
  }

  /** Garante CNES efetivo antes de rotas do app (exceto a própria tela de contexto). */
  ensureCnesContextValid(): Observable<boolean> {
    if (!this.precisaVincularCnesNaNavegacao()) {
      return of(true);
    }
    if (this.getCnesEfetivo()) {
      return of(true);
    }
    return this.loadEstabelecimentos().pipe(
      map((list) => {
        if (list.length === 0) return true;
        if (list.length === 1) {
          this.setCnesSessao(list[0].cnes);
          return true;
        }
        return false;
      })
    );
  }

  rotuloEstabelecimentoCnes(cnes: string): string {
    const list = this.estabelecimentosSubject.value ?? [];
    const hit = list.find((e) => e.cnes === cnes);
    return hit
      ? formatEstabelecimentoOptionLabel(cnes, hit.nomeEstabelecimento)
      : formatEstabelecimentoOptionLabel(cnes, '');
  }
}
